import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  ApplicationType,
  companyIdSelector,
  getApplicationType,
  isBlockingFullscreenLoaderSelector,
  isFullscreenOverlayVisibleAtom,
  isLoggedInAtom,
  isSessionGateLoaderAtom,
  profileDtoAtom,
  profileModalOpenStateAtom,
  SESSION_GATE_LOADER_MS,
  systemSettingsDtoAtom,
} from "@ryde/shared";

import {
  getSessionGatePathname,
  shouldApplySessionGateLoader,
  shouldHideSessionOverlayImmediately,
  shouldHoldSessionGateForUnreadyShell,
  shouldLeaveSessionOverlayLeftBar,
  shouldOpenSessionGateOnCompanyChange,
  shouldShowApiBackdrop,
} from "./use-session-gate-loader.utils";

const useSessionGatePathname = (): string => {
  const [pathname, setPathname] = useState(getSessionGatePathname);

  useEffect(() => {
    const syncPathname = (): void => {
      setPathname(getSessionGatePathname());
    };

    window.addEventListener("hashchange", syncPathname);
    return () => {
      window.removeEventListener("hashchange", syncPathname);
    };
  }, []);

  return pathname;
};

export const useSessionGateLoader = (): void => {
  const pathname = useSessionGatePathname();
  const isSessionGateRoute = shouldApplySessionGateLoader(pathname);
  const isLoggedIn = useRecoilValue(isLoggedInAtom);
  const isProfileModalOpen = useRecoilValue(profileModalOpenStateAtom);
  const profile = useRecoilValue(profileDtoAtom);
  const systemSettings = useRecoilValue(systemSettingsDtoAtom);
  const companyId = useRecoilValue(companyIdSelector);
  const isBlockingFullscreenLoader = useRecoilValue(
    isBlockingFullscreenLoaderSelector,
  );
  const setIsSessionGateLoader = useSetRecoilState(isSessionGateLoaderAtom);

  const applicationType = getApplicationType();
  const isAdminApp = applicationType === ApplicationType.Admin;
  const requireCompanyId = applicationType === ApplicationType.Main;
  const isDialogReady = isProfileModalOpen && Boolean(profile);
  const isShellReady = Boolean(
    profile && systemSettings && (!requireCompanyId || companyId),
  );

  const wasDialogReadyRef = useRef(false);
  const previousCompanyIdRef = useRef(companyId);
  useLayoutEffect(() => {
    if (!isLoggedIn || !isSessionGateRoute) {
      setIsSessionGateLoader(false);
      return;
    }

    if (isDialogReady) {
      setIsSessionGateLoader(false);
      return;
    }

    if (shouldHoldSessionGateForUnreadyShell({ isShellReady })) {
      setIsSessionGateLoader(true);
    }
  }, [
    isDialogReady,
    isLoggedIn,
    isSessionGateRoute,
    isShellReady,
    setIsSessionGateLoader,
  ]);

  useLayoutEffect(() => {
    if (
      wasDialogReadyRef.current &&
      !isDialogReady &&
      isLoggedIn &&
      isSessionGateRoute
    ) {
      setIsSessionGateLoader(true);
    }
    wasDialogReadyRef.current = isDialogReady;
  }, [isDialogReady, isLoggedIn, isSessionGateRoute, setIsSessionGateLoader]);

  useLayoutEffect(() => {
    const previousCompanyId = previousCompanyIdRef.current;
    if (
      shouldOpenSessionGateOnCompanyChange({
        isLoggedIn,
        previousCompanyId,
        companyId,
        isAdminApp,
      })
    ) {
      setIsSessionGateLoader(true);
    }
    previousCompanyIdRef.current = companyId;
  }, [companyId, isAdminApp, isLoggedIn, setIsSessionGateLoader]);

  useEffect(() => {
    if (!isLoggedIn || !isSessionGateRoute || isDialogReady || !isShellReady) {
      return;
    }

    if (isBlockingFullscreenLoader) {
      return;
    }

    const delayMs = SESSION_GATE_LOADER_MS.settleDelayMs;

    const timeoutId = window.setTimeout(() => {
      setIsSessionGateLoader(false);
    }, delayMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    isBlockingFullscreenLoader,
    isDialogReady,
    isLoggedIn,
    isSessionGateRoute,
    isShellReady,
    setIsSessionGateLoader,
  ]);
};

export const useFullscreenOverlayVisibility = (): {
  isSessionOverlayVisible: boolean;
  isApiBackdropVisible: boolean;
  shouldLeaveLeftBar: boolean;
} => {
  useSessionGateLoader();
  const pathname = useSessionGatePathname();
  const isSessionGateRoute = shouldApplySessionGateLoader(pathname);
  const isLoggedIn = useRecoilValue(isLoggedInAtom);
  const isSessionGate = useRecoilValue(isSessionGateLoaderAtom);
  const isBlockingFullscreenLoader = useRecoilValue(
    isBlockingFullscreenLoaderSelector,
  );
  const isProfileModalOpen = useRecoilValue(profileModalOpenStateAtom);
  const profile = useRecoilValue(profileDtoAtom);
  const isDialogReady = isProfileModalOpen && Boolean(profile);
  const shouldShowSessionOverlay =
    isSessionGateRoute && isLoggedIn && isSessionGate && !isDialogReady;
  const [isSessionOverlayVisible, setIsSessionOverlayVisible] = useState(
    shouldShowSessionOverlay,
  );
  const setIsFullscreenOverlayVisible = useSetRecoilState(
    isFullscreenOverlayVisibleAtom,
  );

  useLayoutEffect(() => {
    setIsFullscreenOverlayVisible(isSessionOverlayVisible);
  }, [isSessionOverlayVisible, setIsFullscreenOverlayVisible]);

  useLayoutEffect(() => {
    if (shouldShowSessionOverlay) {
      setIsSessionOverlayVisible(true);
      return;
    }

    if (
      shouldHideSessionOverlayImmediately({
        isLoggedIn,
        isDialogReady,
      })
    ) {
      setIsSessionOverlayVisible(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsSessionOverlayVisible(false);
    }, SESSION_GATE_LOADER_MS.hideDebounceMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isDialogReady, isLoggedIn, shouldShowSessionOverlay]);

  return {
    isSessionOverlayVisible,
    isApiBackdropVisible: shouldShowApiBackdrop({
      isBlockingFullscreenLoader,
      isSessionOverlayVisible,
      isDialogReady,
    }),
    shouldLeaveLeftBar: shouldLeaveSessionOverlayLeftBar(),
  };
};
